import {Button, Card, Col, List, message, Progress, Row, Skeleton, Spin} from "antd";
import {useInjection} from "inversify-react";
import ApiService from "../../services/apiService";
import {CloudflarePagesDeploymentStage, DeployPageApi, DeployPageDto} from "../../api";
import {useCallback, useEffect, useState} from "react";
import styles from "./deployContainer.module.scss";
import moment from "moment";
import {LoadingOutlined} from "@ant-design/icons";

declare const ProgressStatuses: ["normal", "exception", "active", "success"];
const spinIcon = <LoadingOutlined style={{fontSize: 24}} spin/>;

export default function DeployContainer() {
    const apiService = useInjection(ApiService);
    const [projects, setProjects] = useState<Array<DeployPageDto> | null>(null);

    const getProjectsDetails = useCallback(async () => {
        try {
            const response = await apiService.getApi(DeployPageApi).apiProjectDetailsGet();
            setProjects(response.data);
        } catch (e: any) {
            console.log(e.response.data);
        }
    }, [apiService]);

    const isFailure = (deploymentStages: Array<CloudflarePagesDeploymentStage>): boolean => {
        return deploymentStages.filter(ds => ds.status === "failure").length > 0;
    }

    const getPublicEndTime = (deploymentStages: Array<CloudflarePagesDeploymentStage>): string => {
        if (deploymentStages.length) {
            const failure = deploymentStages
                .find(ds => ds.status === "failure");

            if (failure !== undefined) {
                return moment(failure.ended_on!).format("dddd, MMMM Do YYYY, HH:mm:ss");
            }

            const dateEnded = deploymentStages.pop()!.ended_on;
            if (dateEnded !== null) {
                return moment(dateEnded).format("dddd, MMMM Do YYYY, HH:mm:ss");
            } else {
                return "Publikacja w trakcie";
            }
        }
        return "";
    }

    const isDeploymentInProgress = (deploymentStages: Array<CloudflarePagesDeploymentStage>) => {
        return isFailure(deploymentStages)
            || deploymentStages.filter(dp => dp.status === "success").length === deploymentStages.length;
    }

    const getPublicStartTime = (deploymentStages: Array<CloudflarePagesDeploymentStage>): string => {
        if (deploymentStages.length) {
            return moment(deploymentStages[0].started_on).format("dddd, MMMM Do YYYY, HH:mm:ss");
        }
        return "";
    }

    const onPublicClick = async (projectName: string) => {
        message.info("Publikacja projektu w trakcie");
        await apiService.getApi(DeployPageApi).apiDeployGet(projectName);
        getProjectsDetails();
    }

    const getProgressPercent = (stages: Array<CloudflarePagesDeploymentStage>): number => {
        let percent = 0;

        stages.forEach(p => {
            percent += p.status === "success" ? (1 / stages.length!) * 100
                : ((1 / stages.length!) * 100) / 2;
        });

        return Math.round(percent * 100) / 100; //round to 2 decimal places
    }

    const getProgressColor = (stages: Array<CloudflarePagesDeploymentStage>): string => {
        if (isFailure(stages)) {
            return "#FF0000";
        } else if (stages.filter(s => s.status === "success").length === stages.length) {
            return "#52C41A";
        }
        return "#108EE9";
    }

    const getProgressStatus = (stages: Array<CloudflarePagesDeploymentStage>): string => {
        if (isFailure(stages)) {
            return "exception";
        } else if (stages.filter(s => s.status === "success").length === stages.length) {
            return "success"
        }
        return "active";
    }

    useEffect(() => {
        getProjectsDetails();
        const refreshInterval = setInterval(() => getProjectsDetails(), 3000);
        return () => clearInterval(refreshInterval);
    }, [getProjectsDetails]);

    return <>
        <h1>CMS - publikacja</h1>
        {projects !== null ?
            <div className="site-card-wrapper">
                <Row gutter={24}>
                    {projects?.map(p => <Col key={p.projectName} xs={24} md={12} lg={8}>
                        <Card title={p.projectName}>
                            <List>
                                {p.domains?.map((d, id) => <List.Item key={id}>
                                        <a href={`https://${d}`} target="_blank">{d}</a>
                                    </List.Item>
                                )}
                            </List>
                            <br/>
                            <Card title="Publikacja projektu">
                                <div style={{display: "flex"}}>
                                    <Button
                                        type="primary"
                                        disabled={!isDeploymentInProgress(projects?.find(
                                            x => x.projectName === p.projectName)?.latestDeployment?.stages!)}
                                        onClick={() => onPublicClick(p.projectName!)}
                                    >
                                        Publikuj
                                    </Button>
                                    <Spin className={styles.spin} style={{
                                        visibility: !isDeploymentInProgress(projects?.find(
                                            x => x.projectName === p.projectName)?.latestDeployment?.stages!)
                                            ? "visible" : "hidden"
                                    }} indicator={spinIcon}/>
                                </div>
                                <div className={styles.progressContainer}>
                                    <p>Postęp: </p>
                                    <Progress className={styles.progress}
                                              status={getProgressStatus(projects?.find(
                                                  x => x.projectName === p.projectName)?.latestDeployment?.stages!) as
                                                  typeof ProgressStatuses[number]}
                                              percent={getProgressPercent(projects?.find(
                                                  x => x.projectName === p.projectName)?.latestDeployment?.stages!)}
                                              strokeColor={getProgressColor(projects?.find(
                                                  x => x.projectName === p.projectName)?.latestDeployment?.stages!)}
                                              steps={5}
                                    />
                                </div>
                                <br/>
                                <a href={p.latestDeployment?.url!} target="_blank">Podgląd ostatnio
                                    opublikowanej strony</a>
                                <br/>
                                <br/>
                                <div>
                                    <span>Data rozpoczęcia ostatniej publikacji:</span>
                                    <br/>
                                    <strong>{getPublicStartTime(p.latestDeployment?.stages!)}</strong>
                                </div>
                                <div style={{marginTop: "25px"}}>
                                    <span>Data zakończenia ostatniej publikacji:</span>
                                    <br/>
                                    <strong>{getPublicEndTime(p.latestDeployment?.stages!)}</strong>
                                </div>
                            </Card>
                        </Card>
                    </Col>)}
                </Row>
            </div> : <Skeleton active/>}
    </>
}
